import { WING_SHOW_ORIGIN } from './wing-registration';

/**
 * Geometria da arte do personagem (equipamento "default", o visual sem itens). Cada camada é uma folha de animação do legado
 * (`*_game.webp`, 1482×285) com 3 linhas de 11 quadros em células de 114×95 px, o mesmo desenho em todas as camadas.
 * Quais quadros significam o quê está em `character-animator.ts` e em `docs/assets/personagem-animacao.md`.
 * Sem PixiJS aqui, para testar em Node.
 */
export type Sex = 'm' | 'f';

export const SHEET_COLUMNS = 13;
export const SHEET_FRAMES_PER_ROW = 11;
export const SHEET_ROWS = 3;
export const SHEET_WIDTH = 1482;
export const SHEET_HEIGHT = 285;
export const CELL_WIDTH = SHEET_WIDTH / SHEET_COLUMNS;
export const CELL_HEIGHT = SHEET_HEIGHT / SHEET_ROWS;
export const FRAME_COUNT = SHEET_FRAMES_PER_ROW * SHEET_ROWS;

/** Em cada postura: onde ficam os pés dentro da célula (a célula é posicionada por esse ponto) e a altura do desenho, dos pés ao cabelo. */
export interface Posture {
  footX: number;
  height: number;
}
export const STANDING: Posture = { footX: 50, height: 66 };
export const LYING: Posture = { footX: 57, height: 46 };
/** Linha dos pés dentro da célula, igual nas duas posturas. */
export const FOOT_Y = 93;

export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Número de quadro do legado (`GameCharacter`: 3 linhas de 13 quadros) no número daqui (3 linhas de 11 quadros usados): a mesma célula da folha.
 * O legado conta 13 por linha, então o quadro 19 dele é a coluna 6 da linha 1, que aqui é o 17.
 */
export function legacyFrame(index: number): number {
  return Math.floor(index / 13) * SHEET_FRAMES_PER_ROW + (index % 13);
}

export function frameRect(index: number): FrameRect {
  if (!Number.isInteger(index) || index < 0 || index >= FRAME_COUNT) throw new RangeError(`Quadro fora da folha: ${index}`);
  const column = index % SHEET_FRAMES_PER_ROW;
  const row = Math.floor(index / SHEET_FRAMES_PER_ROW);
  return { x: Math.round(column * CELL_WIDTH), y: row * CELL_HEIGHT, width: Math.round(CELL_WIDTH), height: CELL_HEIGHT };
}

/** Caminhos no manifest, de trás para a frente: cabelo de trás, corpo, rosto, cabelo da frente. */
export function defaultLayerPaths(sex: Sex = 'm'): string[] {
  const base = `atlas/images/equip/${sex}`;
  return [
    `${base}/hair/default/1/equip_${sex}_hair_default_1.B_game.webp`,
    `${base}/cloth/default/equip_${sex}_cloth_default.1_game.webp`,
    `${base}/face/default/equip_${sex}_face_default.1_game.webp`,
    `${base}/hair/default/1/equip_${sex}_hair_default_1.A_game.webp`,
  ];
}

/**
 * Peça equipada que pode mudar o visual: categoria e `pic` (pasta da arte) do modelo de item.
 * `fullHair` vale só para o chapéu: é a `Property1` do modelo no legado (`ItemTemplateResponse.showsFullHair`). Com `true` (ou sem chapéu),
 * o cabelo usa a versão `B` (cheia); com `false`, a versão `A` (compacta, feita para ficar sob o chapéu).
 */
export interface WornPiece {
  categoryId: number;
  pic: string;
  fullHair?: boolean;
  /** Id do modelo; as asas usam o nome do quadro `wing_show_<id>`. Sem ele, o id vem do `pic` (`wing002` = 15002). */
  templateId?: number;
}

/** Tela do boneco em pé (quadros `show` dos atlas de equipamento): todas as camadas partilham este tamanho de origem. */
export const SHOW_WIDTH = 250;
export const SHOW_HEIGHT = 312;

/** Um quadro `show` a desenhar: o atlas (JSON) e o nome do quadro dentro dele. */
export interface ShowLayer {
  atlas: string;
  frame: string;
}

/** Categoria do legado (`Shop_Goods.CategoryID`) das peças que aparecem no boneco. */
const CATEGORY = { hat: 1, glasses: 2, hair: 3, eyes: 4, cloth: 5, face: 6, arm: 7, suit: 13, wing: 15 } as const;

/** Pasta da arte de cada categoria. */
const LOOK_FOLDER: Record<number, string> = { 1: 'head', 2: 'glass', 3: 'hair', 4: 'eff', 5: 'cloth', 6: 'face', 13: 'suits' };

/**
 * Partes do boneco em pé, de trás para a frente. É a ordem do cliente do legado (`ShowCharacterLoader.drawCharacter`):
 * rosto, cabelo, roupa, olhos (`eff`), chapéu e óculos. A parte de baixo da lista cobre as de cima.
 */
const Z_ORDER = ['face', 'hair', 'cloth', 'eyes', 'hat', 'glasses'] as const;

type Part = (typeof Z_ORDER)[number];

/**
 * Arma na mão do boneco em pé: o quadro `0/show` do atlas `arm/<pic>/1` (`image/arm/<pic>/1/0/show.png` do legado). O `ShowCharacter` do legado a desenha por último,
 * sobre todas as outras peças (`showGun`); o terno também a mantém. Sem arte no manifest, o boneco fica de mãos vazias.
 */
function weaponLayer(worn: readonly WornPiece[], has: (atlas: string) => boolean): ShowLayer | null {
  const pic = worn.find((w) => w.categoryId === CATEGORY.arm && w.pic)?.pic;
  if (!pic) return null;
  const atlas = `atlas/images/arm/${pic}/1/arm_${pic}_1.json`;
  return has(atlas) ? { atlas, frame: '0/show' } : null;
}

/** Atlas (JSON) das asas `wings.swf` do legado, convertido para quadros. A asa `default` (15001) é um quadro vazio e não tem arte. */
export function wingAtlas(pic: string): string {
  return `atlas/swf/Resource/image/equip/wing/${pic}/wings/Resource_image_equip_wing_${pic}_wings.json`;
}

/** Id do modelo da asa: o informado ou, sem ele, o do `pic` (`wing002` = 15002, `wing31` = 15031). `null` se não dá para saber. */
function wingTemplateId(piece: WornPiece): number | null {
  if (piece.templateId && piece.templateId > 0) return piece.templateId;
  const n = /^wing0*(\d+)$/i.exec(piece.pic)?.[1];
  return n ? 15000 + Number(n) : null;
}

/** Pasta (`pic`) da asa equipada que tem arte, ou `''`. Sem arte no manifest (`has` falso) ou a asa padrão, o boneco fica sem asas. */
export function wingPicOf(worn: readonly WornPiece[], has: (atlas: string) => boolean): string {
  const piece = worn.find((w) => w.categoryId === CATEGORY.wing && w.pic && w.pic !== 'default');
  return piece && has(wingAtlas(piece.pic)) ? piece.pic : '';
}

/**
 * Ampliação da asa em relação ao boneco em pé. O legado (`ShowCharacter.show`) reduz o corpo de 251 para 120 px de largura e deixa a asa em
 * tamanho natural; no quadro `show` do corpo (250 px) isso equivale a ampliar a asa por 251 / 120.
 */
export const WING_SHOW_SCALE = 251 / 120;

/** A asa do boneco em pé: o quadro `wing_show_<id>/1` (as duas asas abertas) e onde ele cai no quadro do corpo, já na escala do corpo. */
export interface WingPlacement {
  atlas: string;
  frame: string;
  /** Canto superior esquerdo da imagem no quadro de `SHOW_WIDTH` × `SHOW_HEIGHT` do corpo (pode ser negativo: a asa passa da borda). */
  x: number;
  y: number;
  /** Ampliação a aplicar ao quadro do atlas (`WING_SHOW_SCALE`). */
  scale: number;
}

/**
 * Asas do boneco em pé, atrás do corpo, a (-1, 2) da origem do boneco (`ShowCharacter.updateWing`), com o canto da imagem no ponto de registro do
 * símbolo (`WING_SHOW_ORIGIN`) e na escala do corpo (`WING_SHOW_SCALE`). `null` sem asa, sem arte no manifest ou sem o ponto de registro.
 */
export function wingShowPlacement(worn: readonly WornPiece[], has: (atlas: string) => boolean): WingPlacement | null {
  const pic = wingPicOf(worn, has);
  const piece = worn.find((w) => w.categoryId === CATEGORY.wing && w.pic === pic);
  const id = piece ? wingTemplateId(piece) : null;
  const origin = WING_SHOW_ORIGIN[pic];
  if (!pic || !id || !origin) return null;
  return { atlas: wingAtlas(pic), frame: `wing_show_${id}/1`, x: (-1 + origin[0]) * WING_SHOW_SCALE, y: (2 + origin[1]) * WING_SHOW_SCALE, scale: WING_SHOW_SCALE };
}

function pieceLayer(sex: Sex, folder: string, pic: string): ShowLayer {
  return { atlas: `atlas/images/equip/${sex}/${folder}/${pic}/equip_${sex}_${folder}_${pic}.json`, frame: '1/show' };
}

/** Cabelo tem uma pasta por variação de cor (usa a `1`) e duas versões de arte: `B` (cheia) e `A` (compacta, para uso com chapéu). */
function hairLayer(sex: Sex, pic: string, version: 'A' | 'B'): ShowLayer {
  return { atlas: `atlas/images/equip/${sex}/hair/${pic}/1/equip_${sex}_hair_${pic}_1.json`, frame: `${version}/show` };
}

/**
 * Versão do cabelo que o legado desenha (`BaseLayer`: `hairType == 1 ? "B" : "A"`, com `hairType` = `Property1` do chapéu).
 * Sem chapéu vale `B`: o chapéu padrão do legado (`default`) tem `Property1` igual a 1.
 */
export function hairVersion(hat: WornPiece | undefined): 'A' | 'B' {
  return hat && !hat.fullHair ? 'A' : 'B';
}

/**
 * Camadas do boneco em pé com as peças equipadas, de trás para a frente (ordem em `Z_ORDER`).
 * Cada camada é desenhada inteira, sem recorte, como no legado. Peça ausente, ou sem arte no manifest (`has` é falso para o atlas), mantém a
 * camada default (cabelo, rosto e roupa) ou some (óculos, chapéu, olhos). O terno é um boneco inteiro (com a cabeça): quando existe, ele é a
 * única camada. Só desenha: os atributos e a propriedade do item não passam por aqui (exceto `fullHair`, que escolhe a versão do cabelo).
 */
export function showLayers(sex: Sex, worn: readonly WornPiece[], has: (atlas: string) => boolean): ShowLayer[] {
  /** Camada da peça equipada na categoria, ou `null` se não há peça ou ela não tem arte. */
  const equipped = (category: number): ShowLayer | null => {
    const pic = worn.find((w) => w.categoryId === category && w.pic)?.pic;
    const layer = pic ? pieceLayer(sex, LOOK_FOLDER[category]!, pic) : null;
    return layer && has(layer.atlas) ? layer : null;
  };

  const arm = weaponLayer(worn, has);
  const suit = equipped(CATEGORY.suit);
  if (suit) return arm ? [suit, arm] : [suit];

  /** Cabelo equipado na versão certa para o chapéu, ou o cabelo default se a peça não existe ou não tem arte. */
  const hairPic = worn.find((w) => w.categoryId === CATEGORY.hair && w.pic)?.pic;
  const version = hairVersion(worn.find((w) => w.categoryId === CATEGORY.hat && w.pic));
  const own = hairPic ? hairLayer(sex, hairPic, version) : null;

  const parts: Record<Part, ShowLayer | null> = {
    face: equipped(CATEGORY.face) ?? pieceLayer(sex, 'face', 'default'),
    hair: own && has(own.atlas) ? own : hairLayer(sex, 'default', version),
    cloth: equipped(CATEGORY.cloth) ?? pieceLayer(sex, 'cloth', 'default'),
    eyes: equipped(CATEGORY.eyes),
    hat: equipped(CATEGORY.hat),
    glasses: equipped(CATEGORY.glasses),
  };

  const layers = Z_ORDER.map((part) => parts[part]).filter((layer): layer is ShowLayer => layer !== null && has(layer.atlas));
  return arm ? [...layers, arm] : layers;
}

/** Aparência de um jogador na partida: sexo, peças de visual equipadas e a pasta da arte da arma (`Pic` do modelo; vazio = sem arma). */
export interface PlayerLook {
  sex: Sex;
  worn: readonly WornPiece[];
  weaponPic: string;
  /** Pasta da asa equipada (`Pic` do modelo); vazio ou ausente = sem asas. */
  wingPic?: string;
}

/** Folha de uma peça de visual na partida (`<pic>.1_game`: a cor padrão, a mesma regra do boneco em pé). */
function gamePiecePath(sex: Sex, folder: string, pic: string): string {
  return `atlas/images/equip/${sex}/${folder}/${pic}/equip_${sex}_${folder}_${pic}.1_game.webp`;
}

function gameHairPath(sex: Sex, pic: string, version: 'A' | 'B'): string {
  return `atlas/images/equip/${sex}/hair/${pic}/1/equip_${sex}_hair_${pic}_1.${version}_game.webp`;
}

/** Folha da arma na partida: `arm/<pic>/1/1/game.png` do legado (a variante `0` é vazia: é a arma "atrás do corpo"). */
export function gameWeaponPath(pic: string): string {
  return `atlas/images/arm/${pic}/1/arm_${pic}_1.1_game.webp`;
}

/**
 * Folhas do boneco na partida, de trás para a frente, com as mesmas regras do boneco em pé (`showLayers`): peça ausente ou sem arte no
 * manifest (`has` falso) mantém o visual padrão (cabelo, rosto, roupa) ou some (olhos, chapéu, óculos, arma). A arma fica **atrás** de tudo:
 * o legado a monta com `gunBack` e desenha o grupo dela (`_faceDown`) antes do rosto e das peças (`GameCharacter.update`, `GameCharacterLoader.drawNormal`).
 * O terno é um boneco inteiro: quando existe, ele substitui todas as peças de visual e só a arma fica por baixo. O cabelo tem duas
 * folhas, `B` atrás e `A` à frente; com chapéu que não mostra o cabelo cheio (`fullHair` falso) só a `A` é desenhada.
 */
export function gameLayerPaths(look: PlayerLook, has: (path: string) => boolean): string[] {
  const { sex, worn } = look;
  const pick = (category: number, folder: string): string | null => {
    const pic = worn.find((w) => w.categoryId === category && w.pic)?.pic;
    const path = pic ? gamePiecePath(sex, folder, pic) : null;
    return path && has(path) ? path : null;
  };
  const weapon = look.weaponPic && has(gameWeaponPath(look.weaponPic)) ? gameWeaponPath(look.weaponPic) : null;

  const suit = pick(13, 'suits');
  if (suit) return weapon ? [weapon, suit] : [suit];

  const hairPic = worn.find((w) => w.categoryId === 3 && w.pic)?.pic;
  const hat = worn.find((w) => w.categoryId === 1 && w.pic);
  const hairOf = (version: 'A' | 'B') => {
    const own = hairPic ? gameHairPath(sex, hairPic, version) : null;
    return own && has(own) ? own : gameHairPath(sex, 'default', version);
  };
  const showFull = hairVersion(hat) === 'B';

  const layers = [
    weapon,
    showFull ? hairOf('B') : null,
    pick(5, 'cloth') ?? gamePiecePath(sex, 'cloth', 'default'),
    pick(6, 'face') ?? gamePiecePath(sex, 'face', 'default'),
    hairOf('A'),
    pick(4, 'eff'),
    pick(1, 'head'),
    pick(2, 'glass'),
  ];
  return layers.filter((path): path is string => path !== null && has(path));
}

/** Chave estável da lista de folhas: jogadores com a mesma aparência compartilham a mesma `CharacterSkin`. */
export const skinKey = (paths: readonly string[]): string => paths.join('|');
